import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import UserSidebar from "./UserSidebar";
import UserHeader from "./UserHeader";
import { apiFetch } from "../api_call";
import { FaUserPlus } from "react-icons/fa";
import "./Dashboard.css";

export default function ManageGrievance() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [grievances, setGrievances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 8;

  const [showEscModal, setShowEscModal] = useState(false);
  const [selectedComplianceId, setSelectedComplianceId] = useState(null);
  const [escalationEmail, setEscalationEmail] = useState("");
  const [reminderDays, setReminderDays] = useState(1);
  const [savingEsc, setSavingEsc] = useState(false);

  const [errors, setErrors] = useState({});
  const [successMsg, setSuccessMsg] = useState("");
  const [successVisible, setSuccessVisible] = useState(false);

  const navigate = useNavigate();

  // -------------------- Fetch grievances --------------------
  useEffect(() => {
    const fetchGrievances = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await apiFetch("/grievance/fetch/custom", { method: "GET" });
        const fetched = Array.isArray(res?.data) ? res.data : [];

        fetched.sort(
          (a, b) => Number(a.grv_compliance_id) - Number(b.grv_compliance_id)
        );

        setGrievances(fetched);
      } catch (err) {
        console.error(err);
        setError("Failed to load grievances.");
      } finally {
        setLoading(false);
      }
    };

    fetchGrievances();
  }, []);

  // -------------------- View --------------------
  const handleView = (id) => {
    localStorage.setItem("selected_grievance_id", id);
    navigate("/edit-grievance");
  };

  // -------------------- Escalation --------------------
  const handleEscalationSave = async () => {
    setErrors({});
    setSuccessMsg("");

    if (!escalationEmail) {
      setErrors({ escalation_email: "Required" });
      return;
    }

    if (!reminderDays || reminderDays < 1) {
      setErrors({ reminder_days: "Must be at least 1" });
      return;
    }

    try {
      setSavingEsc(true);

      const res = await apiFetch("/escalation/grievance", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          compliance_id: selectedComplianceId,
          escalation_email: escalationEmail,
          escalation_reminder_days: Number(reminderDays),
        }),
      });

      if (res?.error) throw res;

      setSuccessMsg("✅ Escalation updated successfully");
      setShowEscModal(false);
      setEscalationEmail("");
      setReminderDays(1);

    } catch (err) {
      console.error(err);
      setErrors({ api: err?.error || "❌ Failed to update escalation" });
    } finally {
      setSavingEsc(false);
    }
  };

  // -------------------- Pagination --------------------
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = grievances.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(grievances.length / rowsPerPage);

  // -------------------- Success auto-hide --------------------
  useEffect(() => {
    if (successMsg) {
      setSuccessVisible(true);
      const timer = setTimeout(() => setSuccessVisible(false), 4500);
      const removeTimer = setTimeout(() => setSuccessMsg(""), 5000);
      return () => {
        clearTimeout(timer);
        clearTimeout(removeTimer);
      };
    }
  }, [successMsg]);

  return (
    <div className="manage-self">
      <UserSidebar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <UserHeader menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

        <main style={{ flex: 1, padding: "1rem 2rem", overflowY: "auto" }}>
          <h2>Manage Grievance</h2>

          <div className="messages-top">
            {errors.api && <div className="login-error">{errors.api}</div>}
            {successMsg && (
              <div className={`success-msg ${!successVisible ? "fade-out" : ""}`}>
                {successMsg}
              </div>
            )}
          </div>

          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Title</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Action</th>
                  <th>Escalation</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" style={{ textAlign: "center" }}>Loading...</td></tr>
                ) : error ? (
                  <tr><td colSpan="6" style={{ textAlign: "center", color: "red" }}>{error}</td></tr>
                ) : grievances.length === 0 ? (
                  <tr><td colSpan="6" style={{ textAlign: "center" }}>No data</td></tr>
                ) : (
                  currentRows.map((item, index) => (
                    <tr key={index}>
                      <td>{item.grv_compliance_id}</td>
                      <td>{item.grv_title}</td>
                      <td>{item.grv_start_date}</td>
                      <td>{item.grv_end_date}</td>

                      <td>
                        <button
                          className="edit-btn"
                          onClick={() => handleView(item.grv_compliance_id)}
                        >
                          View
                        </button>
                      </td>

                      <td>
                        <button
                          onClick={() => {
                            setSelectedComplianceId(item.grv_compliance_id);
                            setShowEscModal(true);
                          }}
                          style={{
                            background: "#1a1744",
                            border: "none",
                            padding: "6px 10px",
                            borderRadius: "4px",
                            cursor: "pointer",
                            color: "#fff",
                          }}
                        >
                          <FaUserPlus size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: "20px", display: "flex", justifyContent: "center" }}>
            <button
              onClick={() => navigate("/add-grievance")}
              style={{
                background: "#1a1744",
                color: "#fff",
                border: "none",
                padding: "10px 16px",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: "500"
              }}
            >
              + Add Grievance
            </button>
          </div>

          <div className="pagination">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Prev</button>
            <span>Page {currentPage} of {totalPages || 1}</span>
            <button disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
          </div>
        </main>
      </div>

      {/* Modal */}
      {showEscModal && (
        <div
          onClick={() => {
            setShowEscModal(false);
            setEscalationEmail("");
            setReminderDays(1);
            setErrors({});
          }}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", padding: "20px", borderRadius: "8px", width: "320px" }}
          >
            <h4>Add Escalation Email</h4>

            <input
              type="email"
              placeholder="Enter escalation email"
              value={escalationEmail}
              onChange={(e) => setEscalationEmail(e.target.value)}
              style={{ width: "100%", padding: "8px", marginTop: "10px" }}
            />

            <input
              type="number"
              min="1"
              placeholder="Reminder days"
              value={reminderDays}
              onChange={(e) => setReminderDays(e.target.value)}
              style={{ width: "100%", padding: "8px", marginTop: "10px" }}
            />

            <button
              onClick={handleEscalationSave}
              disabled={savingEsc}
              style={{
                background: "#1a1744",
                color: "#fff",
                border: "none",
                padding: "8px 12px",
                marginTop: "10px",
                width: "100%",
                cursor: "pointer",
              }}
            >
              {savingEsc ? "Saving..." : "Save Escalation"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
